import { useEffect, useRef, useState } from 'react';
import { EMITTER_CONSTANTS, KEY_RECOIL, STRIP_BEAT_THUMBNAIL_INFO } from '@/shared/config';
import { useEmitter, usePaginationKeyboard } from '@/shared/lib/customHooks';
import { createOnChangePageString } from '@/shared/lib/parsePageInput';
import { BEAT_HR_TAB_ID, selectPageIndex, useReportStore } from '@/entities/report';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';
import { LOAD_BEAT_STATUS } from '@/shared/model';

/**
 * Drives the strip pagination of the RR beat view, deriving the page bounds from the total strip count and the beat thumbnail display limit. It syncs a local page string and input width with the store page index, clamps typed values to the page count, validates them on Enter and blur, cancels in-flight API calls when stepping, binds the Z and X shortcuts while the beat tab area is hovered, and listens to the beat next and previous emitter events.
 *
 * @param isDisabled - Suppresses the keyboard shortcuts while another surface owns input.
 * @returns An object carrying the page string, input size, error flag, page bounds and the previous, next, change, key-down and blur handlers.
 */
const useRrPaginationContainer = (isDisabled?: boolean) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const [isError, setIsError] = useState(false);
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the RR beat view's strip page index. Passes the TAB_1 key with the new page index or updater to the store's setPageIndex action.
   *
   * @param v - The next page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const [inputSize, setInputSize] = useState(String(pageIndex.index + 1).length + 1);
  const [pageString, setPageString] = useState(pageIndex.index + 1);
  const setIsRenderViewerEcg = useReportStore((s) => s.actions.setIsRenderViewerEcg);
  const totalStrip = useReportStore((s) => s.totalStrip);
  const loadingBeatStatus = useReportStore((s) => s.loadingBeatStatus);
  const pageStringRef = useRef(pageString);
  const maxPageNumberRef = useRef<number>();
  const STEP = STRIP_BEAT_THUMBNAIL_INFO.stripDisplayLimit;
  const maxPageNumber = totalStrip ? Math.ceil(totalStrip / STEP) : 1;
  maxPageNumberRef.current = maxPageNumber;
  const isShowPagination = loadingBeatStatus.status !== LOAD_BEAT_STATUS.NONE;
  const isDisabledNext = (pageIndex.index + 1) * STEP >= totalStrip;
  const isDisabledPrev = pageIndex.index === 0;

  /**
   * Steps back the RR beat strip pagination by one page. Cancels in-flight API calls, decrements the store page index and marks the ECG viewer for re-render.
   *
   * @returns Nothing.
   */
  const onClickPrevious = () => {
    cancelAllAPI();
    setPageIndex({ index: pageIndex.index - 1 });
    setIsRenderViewerEcg(true);
  };

  /**
   * Advances the RR beat strip pagination by one page. Cancels in-flight API calls, increments the store page index and marks the ECG viewer for re-render.
   *
   * @returns Nothing.
   */
  const onClickNext = () => {
    cancelAllAPI();
    setPageIndex({ index: pageIndex.index + 1 });
    setIsRenderViewerEcg(true);
  };

  const onChangePageString = createOnChangePageString({
    maxPageNumber,
    setIsError,
    setPageString,
    pageStringRef,
    setInputSize,
    setIsRenderViewerEcg,
  });

  /**
   * Commits the typed page number of the RR beat pagination when the input loses focus. Accepts the value when it parses and stays within the page count, moving the store page index only if it actually changed, otherwise raises the error flag.
   *
   * @param e - The blur event, unused.
   * @returns Nothing.
   */
  const onBlur = (e: any) => {
    if (pageString) {
      const pageNumber = Number(pageString);
      if (pageNumber && pageNumber <= maxPageNumber) {
        const newPageIndex = Number(pageString) - 1;
        setPageIndex((prev) => (newPageIndex !== prev.index ? { index: newPageIndex } : prev));
      } else {
        setIsError(true);
      }
    }
  };

  const { onKeyDown } = usePaginationKeyboard({
    tabZoneId: BEAT_HR_TAB_ID,
    isDisabled,
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    pageStringRef,
    maxPageNumberRef,
    setPageIndex,
    setIsError,
    deps: [pageIndex, totalStrip, isDisabled],
  });

  useEffect(() => {
    setPageString(pageIndex.index + 1);
    setInputSize(String(pageIndex.index + 1).length + 1);
  }, [pageIndex]);

  useEmitter(
    EMITTER_CONSTANTS.BEAT_HR_NEXT,
    () => {
      if (isShowPagination && !isDisabledNext) {
        onClickNext();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.BEAT_HR_PREV,
    () => {
      if (isShowPagination && !isDisabledPrev) {
        onClickPrevious();
      }
    },
    [pageIndex, totalStrip],
  );

  useEffect(() => {
    if (maxPageNumber <= pageIndex.index) {
      setPageIndex({ index: maxPageNumber - 1 });
    }
  }, [maxPageNumber, pageIndex]);

  return {
    pageString,
    inputSize,
    isError,
    maxPageNumber,
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
  };
};

export default useRrPaginationContainer;
